"use client";

/**
 * Filter controls for the calling campaigns: a searchable multi-select with
 * per-option counts, and the row of removable chips that shows everything
 * currently applied. Values inside one filter are OR'd; filters are AND'd.
 */
import * as React from "react";
import { Popover } from "@base-ui/react/popover";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, Search, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { fmtNum } from "@/lib/insights/format";
import { POPUP } from "@/components/staff/insights/ui/DateRangePicker";

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
  /** A colour dot before the label (regions). */
  color?: string;
  hint?: string;
}

export function MultiSelectFilter({
  label, options, selected, onChange, searchable, icon, emptyText = "No options", className, noun,
}: {
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** Defaults to on when there are more than eight options. */
  searchable?: boolean;
  icon?: React.ReactNode;
  emptyText?: string;
  className?: string;
  /** Plural used in the trigger once several are picked, e.g. "3 countries". */
  noun?: string;
}) {
  const [query, setQuery] = React.useState("");
  const showSearch = searchable ?? options.length > 8;
  const visible = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    // Keep picked values reachable even when they have no rows under the other filters.
    const known = new Set(options.map((o) => o.value));
    const extra: FilterOption[] = selected.filter((s) => !known.has(s)).map((s) => ({ value: s, label: s, count: 0 }));
    const all = [...options, ...extra];
    return q ? all.filter((o) => o.label.toLowerCase().includes(q)) : all;
  }, [options, selected, query]);

  const toggle = (v: string) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const active = selected.length > 0;
  const firstLabel = active ? (options.find((o) => o.value === selected[0])?.label ?? selected[0]) : null;
  const summary = !active ? null : selected.length === 1 ? firstLabel : `${selected.length} ${noun ?? "selected"}`;

  return (
    <Popover.Root onOpenChange={(o) => { if (!o) setQuery(""); }}>
      <Popover.Trigger
        className={cn(
          "inline-flex min-h-9 max-w-[16rem] items-center gap-1.5 rounded-xl border px-3 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-[#0f6f7c]",
          active
            ? "border-[#0f6f7c]/40 bg-[#e6f0f1] font-medium text-[#0b5560] hover:bg-[#d9e9eb]"
            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
          className,
        )}>
        {icon}
        <span className="shrink-0">{label}</span>
        {summary && <span className="min-w-0 truncate font-semibold">: {summary}</span>}
        <ChevronDown className="size-3.5 shrink-0 opacity-60" aria-hidden />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner sideOffset={6} align="start" className="z-[65]">
          <Popover.Popup className={cn(POPUP, "w-72 p-0")} aria-label={`${label} filter`}>
            {showSearch && (
              <div className="border-b border-slate-100 p-2">
                <label className="relative block">
                  <span className="sr-only">Search {label.toLowerCase()}</span>
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-slate-400" aria-hidden />
                  <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`}
                    className="min-h-8 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-2 text-sm placeholder:text-slate-400 focus:border-[#0f6f7c] focus:outline-none focus:ring-2 focus:ring-[#0f6f7c]/20" />
                </label>
              </div>
            )}
            <ul role="group" aria-label={label} className="max-h-72 overflow-y-auto p-1">
              {visible.length === 0 && <li className="px-3 py-4 text-center text-sm text-slate-400">{emptyText}</li>}
              {visible.map((o) => {
                const on = selected.includes(o.value);
                return (
                  <li key={o.value}>
                    <button type="button" role="checkbox" aria-checked={on} onClick={() => toggle(o.value)} title={o.hint}
                      className={cn(
                        "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-sm focus-visible:outline-2 focus-visible:outline-[#0f6f7c]",
                        on ? "bg-[#e6f0f1]/60 text-slate-900" : "text-slate-700 hover:bg-slate-50",
                      )}>
                      <span aria-hidden className={cn(
                        "flex size-4 shrink-0 items-center justify-center rounded border",
                        on ? "border-[#0f6f7c] bg-[#0f6f7c] text-white" : "border-slate-300 bg-white",
                      )}>
                        {on && <Check className="size-3" strokeWidth={3} />}
                      </span>
                      {o.color && <span aria-hidden className="size-2.5 shrink-0 rounded-full" style={{ background: o.color }} />}
                      <span className="min-w-0 flex-1 truncate">{o.label}</span>
                      {o.count !== undefined && (
                        <span className={cn("shrink-0 text-xs tabular-nums", o.count ? "text-slate-500" : "text-slate-300")}>{fmtNum(o.count)}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-2 py-1.5">
              <button type="button" disabled={!visible.length}
                onClick={() => onChange(Array.from(new Set([...selected, ...visible.map((o) => o.value)])))}
                className="rounded-md px-2 py-1 text-xs font-medium text-[#007a8a] hover:bg-[#e6f0f1] disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-[#0f6f7c]">
                {query ? "Select matches" : "Select all"}
              </button>
              <button type="button" disabled={!active} onClick={() => onChange([])}
                className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-[#0f6f7c]">
                Clear
              </button>
            </div>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** A single-choice dropdown in the same style, for filters like "contact attempts". */
export function SingleSelectFilter({
  label, options, value, onChange, className,
}: {
  label: string;
  options: FilterOption[];
  value: string | null;
  onChange: (v: string | null) => void;
  className?: string;
}) {
  const current = options.find((o) => o.value === value);
  return (
    <Popover.Root>
      <Popover.Trigger
        className={cn(
          "inline-flex min-h-9 items-center gap-1.5 rounded-xl border px-3 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-[#0f6f7c]",
          current ? "border-[#0f6f7c]/40 bg-[#e6f0f1] font-medium text-[#0b5560]" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
          className,
        )}>
        {label}{current && <span className="font-semibold">: {current.label}</span>}
        <ChevronDown className="size-3.5 opacity-60" aria-hidden />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner sideOffset={6} align="start" className="z-[65]">
          <Popover.Popup className={cn(POPUP, "w-56 p-1")}>
            <ul role="radiogroup" aria-label={label}>
              {[{ value: "", label: "Any" } as FilterOption, ...options].map((o) => {
                const on = (value ?? "") === o.value;
                return (
                  <li key={o.value || "any"}>
                    <Popover.Close
                      role="radio" aria-checked={on} onClick={() => onChange(o.value || null)}
                      className={cn(
                        "flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-left text-sm focus-visible:outline-2 focus-visible:outline-[#0f6f7c]",
                        on ? "bg-[#e6f0f1]/60 font-medium text-slate-900" : "text-slate-700 hover:bg-slate-50",
                      )}>
                      {o.label}
                      {on && <Check className="size-3.5 text-[#0f6f7c]" />}
                    </Popover.Close>
                  </li>
                );
              })}
            </ul>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

export interface ActiveChip {
  id: string;
  group: string;
  label: string;
  onRemove: () => void;
  color?: string;
}

/** Everything applied, one removable chip per value, plus "Clear all". */
export function ActiveFilterChips({ chips, onClearAll }: { chips: ActiveChip[]; onClearAll: () => void }) {
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-label="Active filters">
      <AnimatePresence initial={false}>
      {chips.map((c) => (
        <motion.span key={c.id} layout
          initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.85 }}
          transition={{ duration: 0.15 }}
          className="inline-flex items-center gap-1 rounded-full bg-white py-0.5 pl-2.5 pr-1 text-xs text-slate-700 ring-1 ring-inset ring-slate-200">
          {c.color && <span aria-hidden className="size-2 rounded-full" style={{ background: c.color }} />}
          <span className="text-slate-500">{c.group}:</span>
          <span className="font-medium text-slate-900">{c.label}</span>
          <button type="button" onClick={c.onRemove} aria-label={`Remove ${c.group} ${c.label}`}
            className="inline-flex size-5 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-2 focus-visible:outline-[#0f6f7c]">
            <X className="size-3" />
          </button>
        </motion.span>
      ))}
      </AnimatePresence>
      <button type="button" onClick={onClearAll}
        className="rounded-full px-2.5 py-0.5 text-xs font-medium text-[#007a8a] hover:bg-[#e6f0f1] focus-visible:outline-2 focus-visible:outline-[#0f6f7c]">
        Clear all
      </button>
    </div>
  );
}

/** Chips for one list filter, labelled through its options. */
export function listChips(
  group: string, values: string[], options: FilterOption[], onRemove: (v: string) => void,
): ActiveChip[] {
  return values.map((v) => {
    const o = options.find((x) => x.value === v);
    return { id: `${group}:${v}`, group, label: o?.label ?? v, color: o?.color, onRemove: () => onRemove(v) };
  });
}
